import React from 'react';
import { StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { Placeholder, PlaceholderLine, Fade } from 'rn-placeholder';
import { useSafeArea } from 'react-native-safe-area-context';
import { useIsFocused, NavigationProp } from '@react-navigation/native';
import { SearchBar } from 'react-native-elements';

import { theme } from '@constants';
import { TRedux } from '@reducers';
import { _kappa } from '@reducers/actions';
import { Block, Header, Text, Icon, EndCapButton } from '@components';
import { HeaderHeight, HORIZONTAL_PADDING } from '@services/utils';
import { TUser } from '@backend/auth';
import { isPNM, shouldLoad } from '@services/kappaService';

const UserSkeleton: React.FC = () => {
  return (
    <Block style={styles.skeletonWrapper}>
      <Placeholder Animation={Fade}>
        <PlaceholderLine width={100} />
      </Placeholder>
    </Block>
  );
};

const DirectoryContent: React.FC<{
  navigation: NavigationProp<any, 'Directory'>;
}> = ({ navigation }) => {
  const isFocused = useIsFocused();

  const user = useSelector((state: TRedux) => state.auth.user);
  const loadHistory = useSelector((state: TRedux) => state.kappa.loadHistory);
  const directoryArray = useSelector((state: TRedux) => state.kappa.directoryArray);
  const isGettingEvents = useSelector((state: TRedux) => state.kappa.isGettingEvents);
  const getEventsError = useSelector((state: TRedux) => state.kappa.getEventsError);
  const isGettingDirectory = useSelector((state: TRedux) => state.kappa.isGettingDirectory);
  const getDirectoryError = useSelector((state: TRedux) => state.kappa.getDirectoryError);
  const isGettingAttendance = useSelector((state: TRedux) => state.kappa.isGettingAttendance);
  const getAttendanceError = useSelector((state: TRedux) => state.kappa.getAttendanceError);
  const isGettingExcuses = useSelector((state: TRedux) => state.kappa.isGettingExcuses);
  const getExcusesError = useSelector((state: TRedux) => state.kappa.getExcusesError);
  const getDirectoryErrorMessage = useSelector((state: TRedux) => state.kappa.getDirectoryErrorMessage);

  const [refreshing, setRefreshing] = React.useState<boolean>(
    isGettingEvents || isGettingDirectory || isGettingAttendance
  );

  const dispatch = useDispatch();
  const dispatchGetEvents = React.useCallback(() => dispatch(_kappa.getEvents(user)), [dispatch, user]);
  const dispatchGetMyAttendance = React.useCallback(
    (overwrite: boolean = false) => dispatch(_kappa.getMyAttendance(user, overwrite)),
    [dispatch, user]
  );
  const dispatchGetDirectory = React.useCallback(() => dispatch(_kappa.getDirectory(user)), [dispatch, user]);
  const dispatchGetExcuses = React.useCallback(() => dispatch(_kappa.getExcuses(user)), [dispatch, user]);
  const dispatchSelectUser = React.useCallback((email: string) => dispatch(_kappa.selectUser(email)), [dispatch]);

  const insets = useSafeArea();

  const scrollRef = React.useRef(undefined);

  const loadData = React.useCallback(
    (force: boolean) => {
      if (!isGettingEvents && (force || (!getEventsError && shouldLoad(loadHistory, 'events')))) dispatchGetEvents();
      if (!isGettingDirectory && (force || (!getDirectoryError && shouldLoad(loadHistory, 'directory'))))
        dispatchGetDirectory();
      if (!isGettingAttendance && (force || (!getAttendanceError && shouldLoad(loadHistory, `user-${user.email}`))))
        dispatchGetMyAttendance(force);
      if (!isGettingExcuses && (force || (!getExcusesError && shouldLoad(loadHistory, 'excuses'))))
        dispatchGetExcuses();
    },
    [
      isGettingEvents,
      getEventsError,
      loadHistory,
      dispatchGetEvents,
      isGettingDirectory,
      getDirectoryError,
      dispatchGetDirectory,
      isGettingAttendance,
      getAttendanceError,
      user.email,
      dispatchGetMyAttendance,
      isGettingExcuses,
      getExcusesError,
      dispatchGetExcuses
    ]
  );

  const onRefresh = React.useCallback(() => {
    if (!refreshing) {
      setRefreshing(true);

      loadData(true);
    }
  }, [loadData, refreshing]);

  React.useEffect(() => {
    if (!isGettingEvents && !isGettingDirectory && !isGettingAttendance) {
      setRefreshing(false);
    }
  }, [isGettingEvents, isGettingDirectory, isGettingAttendance]);

  React.useEffect(() => {
    if (isFocused && user.sessionToken) {
      loadData(false);
    }
  }, [isFocused, loadData, user.sessionToken]);

  const keyExtractor = React.useCallback((item: TUser) => item._id, []);

  const renderItem = ({ item }: { item: TUser }) => {
    return (
      <React.Fragment>
        <TouchableOpacity onPress={() => dispatchSelectUser(item.email)}>
          <Block style={styles.userContainer}>
            <Block style={styles.userHeader}>
              <Block style={styles.selectIcon}>
                <Text style={styles.userRole}>{item.role}</Text>
                <Icon family="MaterialIcons" name="keyboard-arrow-right" size={36} color={theme.COLORS.PRIMARY} />
              </Block>
            </Block>

            <Block style={styles.userNameContainer}>
              <Text style={styles.userName}>
                {item.familyName}, {item.givenName}
              </Text>
              {/* {user.privileged === true && !isEmpty(missedMandatory[item.email]) && (
                  <Icon
                    style={styles.mandatoryIcon}
                    family="Feather"
                    name="alert-circle"
                    size={14}
                    color={theme.COLORS.PRIMARY}
                  />
                )} */}
            </Block>
          </Block>
        </TouchableOpacity>
      </React.Fragment>
    );
  };

  const [searchState, setSearchState] = React.useState({
    searchText: '',
    filteredData: []
  });

  const search = (searchText) => {
    let filteredData = directoryArray.filter(function (item) {
      const fullName = item.givenName.toLowerCase() + ' ' + item.familyName.toLowerCase();
      return (
        fullName.includes(searchText.toLowerCase()) ||
        item.phone.includes(searchText.toLowerCase()) ||
        item.email.includes(searchText.toLowerCase())
      );
    });

    setSearchState({ searchText: searchText, filteredData: filteredData });
  };

  const visibleUsers = React.useMemo(
    () => (searchState.filteredData && searchState.filteredData.length > 0 ? searchState.filteredData : directoryArray),
    [searchState.filteredData, directoryArray]
  );

  const brothers = React.useMemo(() => visibleUsers.filter((item: TUser) => !isPNM(item)), [visibleUsers]);
  const pnms = React.useMemo(() => visibleUsers.filter((item: TUser) => isPNM(item)), [visibleUsers]);

  return (
    <Block flex>
      <Header
        leftButton={
          <Block style={[styles.leftButton]}>
            <SearchBar
              round={true}
              autoCapitalize="none"
              autoCorrect={false}
              lightTheme={true}
              showCancel={false}
              cancelIcon={false}
              searchIcon={false}
              placeholder="Search..."
              containerStyle={{
                backgroundColor: 'transparent',
                borderTopColor: 'transparent',
                borderBottomColor: 'transparent',
                width: 150
              }}
              inputContainerStyle={{
                backgroundColor: 'transparent',
                width: 150
              }}
              inputStyle={{
                fontSize: 14
              }}
              onChangeText={(searchText) => search(searchText)}
              value={searchState.searchText}
            />
          </Block>
        }
        title="Directory"
        rightButton={<EndCapButton label="Refresh" loading={refreshing} onPress={onRefresh} />}
      />

      <Block
        style={[
          styles.wrapper,
          {
            top: insets.top + HeaderHeight
          }
        ]}
      >
        {isGettingDirectory && directoryArray.length === 0 ? (
          <Block style={styles.loadingContainer}>
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
            <Block style={styles.separator} />
            <UserSkeleton />
          </Block>
        ) : (
          <FlatList
            ref={(ref) => (scrollRef.current = ref)}
            data={brothers}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            ListFooterComponent={
              pnms.length > 0 ? (
                <React.Fragment>
                  <Text style={styles.pnmsLabel}>PNMs</Text>
                  {pnms.map((item: TUser) => (
                    <React.Fragment key={item._id}>{renderItem({ item })}</React.Fragment>
                  ))}
                </React.Fragment>
              ) : null
            }
            ListEmptyComponent={
              pnms.length > 0 ? null : (
                <React.Fragment>
                  <Text style={styles.pullToRefresh} onPress={onRefresh}>
                    Click to Refresh
                  </Text>
                  <Text style={styles.errorMessage}>{getDirectoryErrorMessage || 'No users'}</Text>
                </React.Fragment>
              )
            }
          />
        )}
      </Block>
    </Block>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0
  },
  loadingContainer: {},
  skeletonWrapper: {
    marginHorizontal: 8,
    marginVertical: 4,
    paddingHorizontal: HORIZONTAL_PADDING,
    paddingVertical: 8,
    backgroundColor: theme.COLORS.WHITE
  },
  container: {
    display: 'flex',
    justifyContent: 'center'
  },
  separator: {
    marginHorizontal: HORIZONTAL_PADDING,
    marginBottom: 16,
    borderBottomColor: theme.COLORS.LIGHT_BORDER,
    borderBottomWidth: 1
  },
  userContainer: {
    marginHorizontal: HORIZONTAL_PADDING,
    height: 48,
    borderBottomColor: theme.COLORS.LIGHT_BORDER,
    borderBottomWidth: 1
  },
  userHeader: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  userName: {
    fontFamily: 'OpenSans',
    fontSize: 16,
    color: theme.COLORS.BLACK
  },
  userNameContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 4,
    backgroundColor: theme.COLORS.WHITE
  },
  mandatoryIcon: {
    marginLeft: 4
  },
  userRole: {
    fontFamily: 'OpenSans-Bold',
    fontSize: 13,
    color: theme.COLORS.GRAY,
    textTransform: 'uppercase'
  },
  selectIcon: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  pnmsLabel: {
    marginTop: 24,
    marginHorizontal: HORIZONTAL_PADDING,
    fontFamily: 'OpenSans-SemiBold',
    fontSize: 13,
    textTransform: 'uppercase',
    color: theme.COLORS.GRAY
  },
  pullToRefresh: {
    marginTop: '50%',
    textAlign: 'center',
    fontFamily: 'OpenSans-SemiBold',
    color: theme.COLORS.PRIMARY
  },
  errorMessage: {
    textAlign: 'center',
    fontFamily: 'OpenSans'
  },
  leftButton: {
    position: 'absolute',
    left: 0
  }
});

export default DirectoryContent;
